import { coversAccessLevel } from '@/helpers/accessLevel';
import { isMenuNode } from '@/navigation/isMenuNode';

/** Catalog kinds that carry assignments (ADR 0005); groups only bundle them. */
const UNIT_KINDS = ['view', 'block'];

function isUnit(node) {
  return UNIT_KINDS.includes(node.kind);
}

function catalogById(catalog) {
  return new Map(catalog.map((node) => [node.id, node]));
}

export function getOwnAssignmentLevel(assignments, nodeId) {
  const own = assignments.find((entry) => entry.node === nodeId);
  return own ? own.level : null;
}

function ancestorsOf(nodeId, catalog, byId) {
  const ancestors = [];
  let current = byId.get(nodeId);
  while (current?.parent) {
    const parent = byId.get(current.parent);
    if (!parent) {
      break;
    }
    ancestors.push(parent);
    current = parent;
  }
  return ancestors;
}

/** Level a role has on a node; nothing is inherited, so it is the own assignment. */
export function getEffectiveLevel(assignments, catalog, nodeId) {
  return getOwnAssignmentLevel(assignments, nodeId);
}

function childrenOf(parentId, catalog) {
  return catalog.filter((node) => node.parent === parentId);
}

function descendantsOf(nodeId, catalog) {
  return childrenOf(nodeId, catalog).flatMap((child) => [
    child,
    ...descendantsOf(child.id, catalog),
  ]);
}

/** The node itself if it is a unit, plus every unit below it. */
function unitsCoveredBy(nodeId, catalog) {
  const node = catalog.find((entry) => entry.id === nodeId);
  const ownUnits = node && isUnit(node) ? [node] : [];
  return [...ownUnits, ...descendantsOf(nodeId, catalog).filter(isUnit)];
}

/**
 * Display state of one matrix checkbox over the node and every unit below it: all cover the
 * level (allow), some do (partial), or none does.
 */
export function getCellState(assignments, catalog, nodeId, level) {
  const units = unitsCoveredBy(nodeId, catalog);
  const coveringUnits = units.filter((unit) =>
    coversAccessLevel(getOwnAssignmentLevel(assignments, unit.id), level)
  );

  if (coveringUnits.length === 0) {
    return 'none';
  }
  return coveringUnits.length === units.length ? 'allow' : 'partial';
}

/** An assigned block shows a hint while its view has no level: the block needs the view. */
export function isMissingViewAssignment(assignments, catalog, nodeId) {
  const node = catalog.find((entry) => entry.id === nodeId);
  if (!node || node.kind !== 'block') {
    return false;
  }
  const hasBlockLevel = getOwnAssignmentLevel(assignments, nodeId) !== null;
  const hasViewLevel = getOwnAssignmentLevel(assignments, node.viewId) !== null;
  return hasBlockLevel && !hasViewLevel;
}

/**
 * Sets the level (null removes it) on the node, if it is a unit, and on every unit below it.
 * Other assignments stay. Returns positive assignments on units only (save payload).
 */
export function applyCellLevelChange(assignments, catalog, nodeId, level) {
  const unitIds = unitsCoveredBy(nodeId, catalog).map((unit) => unit.id);
  const unitIdSet = new Set(unitIds);
  const otherAssignments = assignments.filter((entry) => !unitIdSet.has(entry.node));
  if (!level) {
    return otherAssignments;
  }
  return [...otherAssignments, ...unitIds.map((unitId) => ({ node: unitId, level }))];
}

const LEVEL_BELOW = {
  viewer: null,
  editor: 'viewer',
};

/** Checkbox click: a checked level is lowered to the level below it, an unchecked one granted. */
export function toggleCellLevel(assignments, catalog, nodeId, level) {
  const isChecked = getCellState(assignments, catalog, nodeId, level) === 'allow';
  const wantedLevel = isChecked ? LEVEL_BELOW[level] : level;
  return applyCellLevelChange(assignments, catalog, nodeId, wantedLevel);
}

function isHiddenByCollapse(nodeId, catalog, byId, collapsed) {
  return ancestorsOf(nodeId, catalog, byId).some((ancestor) => collapsed[ancestor.id]);
}

/** Matrix rows in catalog order; descendants of collapsed nodes are left out. */
export function getVisibleRows(catalog, collapsed) {
  const byId = catalogById(catalog);

  return catalog
    .filter((node) => !isHiddenByCollapse(node.id, catalog, byId, collapsed))
    .map((node) => ({
      node,
      depth: ancestorsOf(node.id, catalog, byId).length,
      hasChildren: childrenOf(node.id, catalog).length > 0,
    }));
}

function isVisibleView(node, assignments) {
  return node.kind === 'view' && getOwnAssignmentLevel(assignments, node.id) !== null;
}

function hasVisibleViewBelow(nodeId, assignments, catalog) {
  return descendantsOf(nodeId, catalog).some((node) => isVisibleView(node, assignments));
}

/** Menu of a role: the views it can open, plus the groups needed to reach them. */
export function getMenuPreviewRows(assignments, catalog) {
  return getVisibleRows(catalog, {}).filter(
    ({ node }) =>
      isMenuNode(node) &&
      (isVisibleView(node, assignments) || hasVisibleViewBelow(node.id, assignments, catalog))
  );
}

export function diffRoleAssignments(saved, edited) {
  const normalize = (list) =>
    [...list]
      .map((entry) => `${entry.node}:${entry.level}`)
      .sort()
      .join('|');

  return { changed: normalize(saved) !== normalize(edited) };
}
